import {
  Alert,
  Box,
  Button,
  Card,
  CardMedia,
  Chip,
  CircularProgress,
  Collapse,
  Container,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Pagination,
  Select as MuiSelect,
  Stack,
  TextField,
  Tooltip,
  Typography
} from "@mui/material";
import { Refresh, ExpandMore, ExpandLess, CalendarMonth, LocationOn } from "@mui/icons-material";
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import Page from "src/components/Page";
import { Routes } from "src/constant/api_url";
import { fetchPendingStockOverview } from "src/utils/common";
import CustomLoader from "src/commonComponents/CustomLoader";
import { notification } from "src/utils/messages";

// No context needed — admin only, uses localStorage user
export default function PendingStock() {
  const navigate = useNavigate();
  const loginUser = JSON.parse(localStorage.getItem("userData"));

  // Guard — only admin (u_type==0) or subadmin p_user_id==1 with is_subadmin/management_p
  const isAdmin =
    loginUser?.u_type == 0 ||
    (loginUser?.u_type == 2 && loginUser?.p_user_id == 1 && (loginUser?.is_subadmin == 1 || loginUser?.management_p != 0)) ||
    (loginUser?.p_user_id == 1 && loginUser?.u_type == 2 && loginUser?.is_subadmin == 1);
  // Fallback simple: spec says u_type==0 or (u_type==2 && p_user_id==1)
  const isAdminSimple = loginUser?.u_type == 0 || (loginUser?.u_type == 2 && loginUser?.p_user_id == 1);
  const canAccess = isAdmin || isAdminSimple;

  const [filters, setFilters] = useState({ city_id: -1, p_id: -1, search: "" });
  const [searchInput, setSearchInput] = useState("");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [data, setData] = useState([]);
  const [counter, setCounter] = useState(0);
  const [totalPendingQty, setTotalPendingQty] = useState(0);
  const [filterMeta, setFilterMeta] = useState({ cities: [], products: [] });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const fetchData = useCallback(async () => {
    if (!canAccess) return;
    setLoading(true);
    setError("");
    try {
      const res = await fetchPendingStockOverview({
        city_id: filters.city_id,
        p_id: filters.p_id,
        search: filters.search,
        page,
        per_page: perPage,
        s_status: 0
      });
      if (!res) {
        setError("Failed to fetch pending stock");
        setLoading(false);
        return;
      }
      if (res.status == 0) {
        // Check for admin access required
        if ((res.message || "").toLowerCase().includes("admin")) {
          setError(res.message);
        } else {
          const notify = notification({ type: "error", message: res.message || "Failed to fetch" });
          notify();
        }
        setData([]);
        setCounter(0);
        setTotalPendingQty(0);
        // still update filter_meta if present
        if (res.data?.filter_meta) setFilterMeta(res.data.filter_meta);
        setLoading(false);
        return;
      }
      // success — handle multiple backend shapes robustly
      // res = {status:1, message, data: ...} where data may be {data:[...], counter, filter_meta} OR {data:[...]} OR [...]
      const apiData = res.data !== undefined ? res.data : res;
      let rows = [];
      let counterVal = 0;
      let totalQty = 0;
      let meta = null;

      if (Array.isArray(apiData)) {
        // direct array
        rows = apiData;
      } else if (Array.isArray(apiData?.data)) {
        // {data: [...]}
        rows = apiData.data;
        counterVal = apiData.counter ?? apiData.total ?? rows.length;
        totalQty = apiData.total_pending_qty ?? apiData.totalPendingQty ?? rows.reduce((s, r) => s + Number(r.total_pending || 0), 0);
        meta = apiData.filter_meta || apiData.filterMeta || null;
      } else if (apiData && typeof apiData === "object") {
        // fallback — maybe nested differently
        rows = apiData.data && Array.isArray(apiData.data) ? apiData.data : [];
        if (rows.length === 0 && Array.isArray(apiData)) rows = apiData;
        counterVal = apiData.counter ?? rows.length;
        totalQty = apiData.total_pending_qty ?? rows.reduce((s, r) => s + Number(r.total_pending || 0), 0);
        meta = apiData.filter_meta || null;
      }

      // If backend didn't send filter_meta, derive from rows (required for dropdowns)
      if (!meta || (!meta.cities?.length && !meta.products?.length)) {
        const cityMap = new Map();
        const prodMap = new Map();
        rows.forEach((r) => {
          if (r.p_id != null) prodMap.set(String(r.p_id), { p_id: r.p_id, p_name: r.p_name });
          (r.cities || []).forEach((c) => {
            if (c.city_id != null) cityMap.set(String(c.city_id), { city_id: c.city_id, city_name: c.city_name });
          });
        });
        meta = {
          cities: Array.from(cityMap.values()),
          products: Array.from(prodMap.values())
        };
      }

      setData(rows);
      setCounter(counterVal || rows.length);
      setTotalPendingQty(totalQty);
      if (meta) setFilterMeta(meta);
    } catch (e) {
      setError(e?.response?.data?.message || e.message || "Network error");
    }
    setLoading(false);
  }, [filters.city_id, filters.p_id, filters.search, page, perPage, canAccess]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Debounced search — optional Search button version per spec; we support both
  // Search button triggers setFilters from searchInput
  const handleSearch = () => {
    setPage(1);
    setFilters((prev) => ({ ...prev, search: searchInput.trim() }));
  };
  const handleClear = () => {
    setSearchInput("");
    setFilters({ city_id: -1, p_id: -1, search: "" });
    setPage(1);
    setPerPage(10);
  };

  const handleCityChange = (opt) => {
    setPage(1);
    setFilters((prev) => ({ ...prev, city_id: opt ? opt.value : -1 }));
  };
  const handleProductChange = (opt) => {
    setPage(1);
    setFilters((prev) => ({ ...prev, p_id: opt ? opt.value : -1 }));
  };

  const [expandedId, setExpandedId] = useState(null);

  const toggleExpand = (pId) => setExpandedId((prev) => (prev === pId ? null : pId));

  if (!canAccess) {
    return (
      <Page title="Pending Stock | LVB">
        <Container>
          <Alert severity="error" sx={{ mt: 3 }}>
            Admin access required. You do not have permission to view Pending Stock Overview.
          </Alert>
          <Button sx={{ mt: 2 }} variant="contained" onClick={() => navigate("/dashboard")}>
            Back to Dashboard
          </Button>
        </Container>
      </Page>
    );
  }

  const cityOptions = [{ value: -1, label: "All Cities" }, ...filterMeta.cities.map((c) => ({ value: c.city_id, label: c.city_name }))];
  const productOptions = [{ value: -1, label: "All Products" }, ...filterMeta.products.map((p) => ({ value: p.p_id, label: p.p_name }))];

  const selectedCity = cityOptions.find((o) => o.value === filters.city_id) || cityOptions[0];
  const selectedProduct = productOptions.find((o) => o.value === filters.p_id) || productOptions[0];

  return (
    <Page title="Pending Stock | LVB">
      <Container maxWidth="xl">
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2, flexWrap: "wrap", gap: 2 }}>
          <Typography variant="h4">Pending Stock</Typography>
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              startIcon={loading ? <CircularProgress size={16} /> : <Refresh fontSize="small" />}
              onClick={fetchData}
              disabled={loading}
            >
              Refresh
            </Button>
            <Button variant="contained" onClick={() => navigate("/citystockreport")}>
              City Stock Report
            </Button>
          </Stack>
        </Stack>

        {/* Instruction */}
        <Alert
          severity="info"
          sx={{
            mb: 2,
            bgcolor: "#f8fafc",
            border: "1px solid #e2e8f0",
            color: "#334155",
            borderRadius: 2
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 600, color: "#0f172a" }} component="span">
            Instruction:
          </Typography>{" "}
          <Typography variant="body2" component="span" sx={{ color: "#475569" }}>
            This overview shows stock awaiting city acceptance. Use City / Product filters — dropdowns list only cities/products that actually have pending stock. Click a card to expand city breakdown.
          </Typography>
        </Alert>

        <Card sx={{ p: 2, mb: 2 }}>
          <Grid container spacing={2} alignItems="flex-end">
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="caption" sx={{ fontWeight: 700, mb: 0.5, display: "block" }}>
                Filter by City
              </Typography>
              <Select
                value={selectedCity}
                onChange={handleCityChange}
                options={cityOptions}
                placeholder="All Cities"
                isClearable={false}
                menuPortalTarget={document.body}
                styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="caption" sx={{ fontWeight: 700, mb: 0.5, display: "block" }}>
                Filter by Product
              </Typography>
              <Select
                value={selectedProduct}
                onChange={handleProductChange}
                options={productOptions}
                placeholder="All Products"
                isClearable={false}
                menuPortalTarget={document.body}
                styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="caption" sx={{ fontWeight: 700, mb: 0.5, display: "block" }}>
                Search Product
              </Typography>
              <TextField
                size="small"
                fullWidth
                placeholder="Search product name..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSearch()}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Stack direction="row" spacing={1} sx={{ height: 40 }}>
                <Button variant="contained" onClick={handleSearch} disabled={loading} fullWidth>
                  Search
                </Button>
                <Button variant="outlined" onClick={handleClear} disabled={loading} fullWidth>
                  Clear
                </Button>
              </Stack>
            </Grid>
          </Grid>
          {/* Active filters summary */}
          {(filters.city_id !== -1 || filters.p_id !== -1 || filters.search) && (
            <Stack direction="row" spacing={1} sx={{ mt: 2, flexWrap: "wrap", gap: 1 }}>
              <Typography variant="caption" color="text.secondary" sx={{ lineHeight: "24px" }}>
                Active filters:
              </Typography>
              {filters.city_id !== -1 && <Chip size="small" label={`City: ${selectedCity.label}`} onDelete={() => handleCityChange({ value: -1 })} />}
              {filters.p_id !== -1 && <Chip size="small" label={`Product: ${selectedProduct.label}`} onDelete={() => handleProductChange({ value: -1 })} color="primary" />}
              {filters.search && <Chip size="small" label={`Search: ${filters.search}`} onDelete={() => { setSearchInput(""); setFilters((p) => ({ ...p, search: "" })); setPage(1); }} />}
            </Stack>
          )}
        </Card>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CustomLoader />
          </Box>
        ) : data.length > 0 ? (
          <>
            <Grid container spacing={3} alignItems="flex-start">
              {data.map((item, idx) => {
                const expanded = expandedId === item.p_id;
                const serial = (page - 1) * perPage + idx + 1;
                return (
                  <Grid item xs={12} sm={6} md={6} lg={4} key={item.p_id}>
                    <Card
                      sx={{
                        borderRadius: 3,
                        border: "1px solid #e2e8f0",
                        borderLeft: "3px solid #f59e0b",
                        overflow: "hidden",
                        display: "flex",
                        flexDirection: "column",
                        transition: "all 0.2s ease",
                        "&:hover": {
                          transform: "translateY(-3px)",
                          boxShadow: "0 8px 24px rgba(15,23,42,0.08), 0 2px 8px rgba(15,23,42,0.06)",
                          borderColor: "#cbd5e1"
                        }
                      }}
                    >
                      {/* Header: No + Product */}
                      <Stack direction="row" spacing={1.6} alignItems="center" sx={{ p: 2, pb: 1.6 }}>
                        <Box
                          sx={{
                            width: 28,
                            height: 28,
                            borderRadius: "50%",
                            bgcolor: "#fffbeb",
                            border: "1px solid #fde68a",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: "0.72rem",
                            fontWeight: 700,
                            color: "#92400e",
                            flexShrink: 0
                          }}
                        >
                          {serial}
                        </Box>
                        <CardMedia
                          component="img"
                          image={
                            item.p_image
                              ? `${Routes.API_HOSTNAME}products-image/${item.p_image}`
                              : "https://dummyimage.com/200x200/cccccc/000000&text=No+Img"
                          }
                          alt={item.p_name}
                          sx={{
                            width: 52,
                            height: 52,
                            borderRadius: 2,
                            objectFit: "contain",
                            bgcolor: "#fff",
                            border: "1px solid #f1f5f9",
                            flexShrink: 0
                          }}
                        />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "#0f172a", lineHeight: 1.2, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                            {item.p_name}
                          </Typography>
                          <Typography variant="caption" sx={{ color: "#64748b", fontSize: "0.72rem" }}>
                            {item.p_type === 0 ? "Product" : item.p_type === 1 ? "Pin" : item.p_type === 2 ? "Welcome Kit" : "Product"} • {item.p_image ? item.p_image.slice(0, 18) : ""}
                          </Typography>
                        </Box>
                        <Tooltip title={expanded ? "Collapse" : "View city breakdown"}>
                          <IconButton
                            size="small"
                            onClick={() => toggleExpand(item.p_id)}
                            sx={{
                              width: 30,
                              height: 30,
                              bgcolor: expanded ? "#fef3c7" : "#fff",
                              border: expanded ? "1px solid #fde68a" : "1px solid #e2e8f0",
                              color: expanded ? "#92400e" : "#64748b",
                              "&:hover": { bgcolor: expanded ? "#fde68a" : "#f8fafc" }
                            }}
                          >
                            {expanded ? <ExpandLess sx={{ fontSize: 18 }} /> : <ExpandMore sx={{ fontSize: 18 }} />}
                          </IconButton>
                        </Tooltip>
                      </Stack>

                      {/* Stats row */}
                      <Stack direction="row" spacing={1} sx={{ px: 2, pb: 1.2, flexWrap: "wrap", gap: 1 }}>
                        <Chip
                          label={`${item.total_pending} pending`}
                          size="small"
                          sx={{ bgcolor: "#fef3c7", color: "#92400e", border: "1px solid #fde68a", fontWeight: 700, fontSize: "0.72rem", height: 22 }}
                        />
                        <Chip
                          icon={<LocationOn sx={{ fontSize: 14 }} />}
                          label={`${item.city_count} ${item.city_count === 1 ? "city" : "cities"}`}
                          size="small"
                          variant="outlined"
                          sx={{ fontSize: "0.72rem", height: 22, bgcolor: "#f8fafc" }}
                        />
                      </Stack>

                      {/* Dates */}
                      <Stack direction="row" spacing={1.4} sx={{ px: 2, pb: 1.4 }} flexWrap="wrap">
                        <Stack direction="row" alignItems="center" spacing={0.5}>
                          <CalendarMonth sx={{ fontSize: 14, color: "#94a3b8" }} />
                          <Typography variant="caption" sx={{ color: "#475569", fontSize: "0.72rem" }}>
                            Earliest: <strong style={{ color: "#0f172a" }}>{item.earliest_date || "-"}</strong>
                          </Typography>
                        </Stack>
                        <Stack direction="row" alignItems="center" spacing={0.5}>
                          <CalendarMonth sx={{ fontSize: 14, color: "#94a3b8" }} />
                          <Typography variant="caption" sx={{ color: "#475569", fontSize: "0.72rem" }}>
                            Latest: <strong style={{ color: "#0f172a" }}>{item.latest_date || "-"}</strong>
                          </Typography>
                        </Stack>
                      </Stack>

                      {/* View breakdown trigger — chips only inside Collapse */}
                      {!expanded && (item.cities || []).length > 0 && (
                        <Box sx={{ px: 2, pb: 1.4 }}>
                          <Button
                            size="small"
                            variant="text"
                            onClick={() => toggleExpand(item.p_id)}
                            sx={{ fontSize: "0.72rem", p: 0, minWidth: 0, color: "#d97706", fontWeight: 600, textTransform: "none" }}
                          >
                            View city breakdown ({item.city_count} {item.city_count === 1 ? "city" : "cities"}) →
                          </Button>
                        </Box>
                      )}

                      {/* Expandable city breakdown */}
                      <Collapse in={expanded} timeout="auto" unmountOnExit>
                        <Box sx={{ px: 2, pb: 1.6, pt: 1.4, bgcolor: "#fffbeb", borderTop: "1px solid #fde68a" }}>
                          <Typography variant="caption" sx={{ fontWeight: 700, color: "#92400e", letterSpacing: "0.04em", textTransform: "uppercase", fontSize: "0.68rem", mb: 1, display: "block" }}>
                            City breakdown — {item.p_name}
                          </Typography>
                          <Stack direction="row" flexWrap="wrap" gap={0.8}>
                            {(item.cities || []).map((c) => (
                              <Chip
                                key={c.city_id}
                                label={`${c.city_name}: ${c.pending} (${c.date})`}
                                size="small"
                                onClick={() =>
                                  navigate("/citystockreport", {
                                    state: {
                                      pendingCityId: c.city_id,
                                      pendingCityName: c.city_name,
                                      pendingProduct: item,
                                      autoOpenHistory: true,
                                      p_id: item.p_id,
                                      p_name: item.p_name,
                                      p_image: item.p_image,
                                      p_type: item.p_type,
                                      city_id: c.city_id,
                                      city_name: c.city_name
                                    }
                                  })
                                }
                                sx={{ bgcolor: "#fff", border: "1px solid #f59e0b", fontWeight: 600, cursor: "pointer", height: 24 }}
                              />
                            ))}
                          </Stack>
                        </Box>
                      </Collapse>

                      {/* Actions footer — View Product only */}
                      <Stack
                        direction="row"
                        spacing={1}
                        sx={{
                          p: 1.2,
                          mt: "auto",
                          borderTop: "1px solid #f1f5f9",
                          bgcolor: "#f8fafc",
                          alignItems: "center",
                          justifyContent: "flex-start"
                        }}
                      >
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() =>
                            navigate("/products", {
                              state: {
                                autoOpenHistory: true,
                                p_id: item.p_id,
                                p_name: item.p_name,
                                p_image: item.p_image,
                                p_type: item.p_type
                              }
                            })
                          }
                          sx={{ fontSize: "0.72rem", py: 0.3, borderRadius: "8px", textTransform: "none", fontWeight: 600 }}
                        >
                          View Product
                        </Button>
                      </Stack>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>

            {/* Pagination */}
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 3, flexWrap: "wrap", gap: 1.5, p: 1 }}>
              <Stack direction="row" alignItems="center" spacing={1.2}>
                <Typography variant="caption" sx={{ color: "#64748b" }}>
                  Rows per page:
                </Typography>
                <FormControl size="small" sx={{ minWidth: 80 }}>
                  <MuiSelect
                    value={perPage}
                    onChange={(e) => {
                      setPerPage(e.target.value);
                      setPage(1);
                    }}
                    size="small"
                    sx={{ height: 32, fontSize: "0.8rem", borderRadius: "8px", bgcolor: "#fff" }}
                  >
                    {[10, 25, 50].map((n) => (
                      <MenuItem key={n} value={n} sx={{ fontSize: "0.8rem" }}>
                        {n}
                      </MenuItem>
                    ))}
                  </MuiSelect>
                </FormControl>
                <Typography variant="caption" sx={{ color: "#475569", ml: 1 }}>
                  {counter === 0 ? "0 of 0" : `${(page - 1) * perPage + 1}–${Math.min(page * perPage, counter)} of ${counter}`}
                </Typography>
              </Stack>
              <Pagination
                count={Math.max(1, Math.ceil(counter / perPage))}
                page={page}
                onChange={(_, p) => setPage(p)}
                color="primary"
                size="small"
                showFirstButton
                showLastButton
                sx={{
                  "& .MuiPaginationItem-root": { fontSize: "0.8rem", borderRadius: "8px" },
                  "& .Mui-selected": { bgcolor: "#0f172a", color: "#fff", "&:hover": { bgcolor: "#1e293b" } }
                }}
              />
            </Stack>
          </>
        ) : (
          <Card sx={{ p: 4, textAlign: "center", border: "1px dashed #cbd5e1", bgcolor: "#f8fafc" }}>
            <Typography variant="h6" color="text.secondary">
              No pending stock found
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {filters.city_id !== -1 || filters.p_id !== -1 || filters.search ? "Try clearing filters" : "All stock has been accepted"}
            </Typography>
          </Card>
        )}
      </Container>
    </Page>
  );
}
